import React, { useEffect, useRef } from 'react';

const Audiogram = ({ analyser, barCount = 80, className = '' }) => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    if (!analyser || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Set canvas size based on container
    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = rect.height * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resizeCanvas();

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    // Half the bars (we'll mirror them)
    const halfBars = Math.floor(barCount / 2);

    // Smoothed values for organic animation
    const smoothedBars = new Array(halfBars).fill(0);
    const smoothingFactor = 0.12; // Lower = smoother, more elegant motion

    const draw = () => {
      const width = canvas.width / window.devicePixelRatio;
      const height = canvas.height / window.devicePixelRatio;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Get frequency data
      analyser.getByteFrequencyData(dataArray);

      // Calculate bar dimensions - thin elegant lines
      const gap = 2;
      const barWidth = 2; // Very thin bars
      const totalBarSpace = barCount * barWidth + (barCount - 1) * gap;
      const centerX = width / 2;
      const maxBarHeight = height * 0.85;

      // Subtle glow effect
      ctx.shadowColor = 'rgba(227, 6, 19, 0.4)';
      ctx.shadowBlur = 6;

      // Sample frequency bins (focus on voice range - lower frequencies)
      const step = Math.max(1, Math.floor(dataArray.length / 2 / halfBars));

      for (let i = 0; i < halfBars; i++) {
        // Average a range of frequency bins
        let sum = 0;
        const start = i * step;
        const end = Math.min(start + step, dataArray.length);
        for (let j = start; j < end; j++) {
          sum += dataArray[j];
        }
        const avg = sum / (end - start);

        // Normalize to 0-1 with subtle boost
        const targetValue = Math.pow(avg / 255, 0.8);

        // Smooth the transition
        smoothedBars[i] += (targetValue - smoothedBars[i]) * smoothingFactor;

        // Calculate bar height with minimum visibility
        const barHeight = Math.max(smoothedBars[i] * maxBarHeight, 3);

        // Opacity based on intensity for depth
        const intensity = smoothedBars[i];
        const alpha = 0.5 + intensity * 0.5;

        ctx.fillStyle = `rgba(227, 6, 19, ${alpha})`;

        // Draw bar on right side (from center outward)
        const rightX = centerX + (gap / 2) + i * (barWidth + gap);
        const y = (height - barHeight) / 2;
        ctx.beginPath();
        ctx.roundRect(rightX, y, barWidth, barHeight, barWidth / 2);
        ctx.fill();

        // Draw mirrored bar on left side (from center outward)
        const leftX = centerX - (gap / 2) - barWidth - i * (barWidth + gap);
        ctx.beginPath();
        ctx.roundRect(leftX, y, barWidth, barHeight, barWidth / 2);
        ctx.fill();
      }

      animationFrameRef.current = requestAnimationFrame(draw);
    };

    draw();

    // Handle resize
    window.addEventListener('resize', resizeCanvas);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [analyser, barCount]);

  return (
    <canvas
      ref={canvasRef}
      className={`w-full ${className}`}
      style={{ display: 'block' }}
    />
  );
};

export default Audiogram;
